import navCamera from "../../../../assets/brand/edit-hub/nav-camera.svg";
import navPersonal from "../../../../assets/brand/edit-hub/nav-personal.svg";
import navSport from "../../../../assets/brand/edit-hub/nav-sport.svg";
import navSchool from "../../../../assets/brand/edit-hub/nav-school.svg";
import navLinks from "../../../../assets/brand/edit-hub/nav-links.svg";
import navAcademics from "../../../../assets/brand/edit-hub/nav-academics.svg";
import navHonors from "../../../../assets/brand/edit-hub/nav-honors.svg";
import navWork from "../../../../assets/brand/edit-hub/nav-work.svg";
import navPrivacy from "../../../../assets/brand/edit-hub/nav-privacy.svg";

// Edit profile · Hub model (Figma "Athlete Edit Profile", approved 18 Sep
// 2026). The sidebar lists the sections with their progress, the open
// section renders on the right and saves as it goes. Every "done" check
// below is one line of the section's "N of M done" count; the first
// unfinished line of the first unfinished section is "Up next".
//
// Only what the hub asks for counts: a field the hub marks Optional (the
// bio, interests, jersey number, other sports, previous teams and schools,
// GPA, NIL deals) is never a line, and a line with a fourth entry, `applies`,
// counts only for the athletes it applies to (the club is optional for a
// college athlete, the last high school is asked of college and pro
// athletes, the college level of college athletes, the weight of men only).
// A line's text (its second entry) is a string, or a function of the
// athlete for the Teams lines that name the level's team (lineItem). The
// bio source line is always done: the hub shows Cache-generated selected
// until the athlete picks, so an unset source is that default choice.
// backend users/profile_strength.py mirrors these lines, word for word;
// keep the two in step.

export const has = (v) =>
  v !== undefined && v !== null && !(typeof v === "number" && v <= 0) && !(typeof v === "string" && v.trim() === "") &&
  !(Array.isArray(v) && v.length === 0) && !(typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);

export const SOCIAL_NETWORKS = ["instagram", "tiktok", "youtube", "x"];

export const normalizeUrl = (raw) => {
  const v = (raw || "").trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

// Which platform a saved link belongs to. Anything that is a URL but not a
// known network counts as a sports or press link.
export const platformOf = (url) => {
  try {
    const h = new URL(normalizeUrl(url)).hostname.replace(/^www\./, "");
    if (h.includes("instagram.com")) return "instagram";
    if (h.includes("tiktok.com")) return "tiktok";
    if (h.includes("youtube.com") || h.includes("youtu.be")) return "youtube";
    if (h === "x.com" || h.endsWith(".x.com") || h.includes("twitter.com")) return "x";
    if (h.includes("hudl.com")) return "hudl";
    if (h === "247sports.com" || h.endsWith(".247sports.com")) return "247sports";
    if (h === "on3.com" || h.endsWith(".on3.com")) return "on3";
    if (h.includes("the-passport.com")) return "passport";
    return "other";
  } catch {
    return null;
  }
};

// Level of play: 1 high school, 2 college, 3 pro (0 or blank: not picked).
const levelOf = (a) => Number(a?.levelOfPlay) || 0;
export const isCollegeAthlete = (a) => levelOf(a) === 2;

// The gender code (type 'gender') of a man: 1 (2 is a woman, 0 or blank not
// given). Weight is asked of men only (Kobi, 2026-10-07: "Weight (males
// only)"): their weight line counts and a bio weight fills theirs, never a
// woman's or one with no gender yet. backend profile_strength.is_male.
export const MALE = 1;
export const isMale = (a) => Number(a?.gender) === MALE;

// What each kind of team is called (the server's `kind`, backend team_kinds:
// college, scholastic, club, pro), on the profile's team chips and the
// Teams card; Junior for an 8th grade and under team (the server's
// `junior`, team_kinds.is_junior). '' for a team nobody knows the kind of.
export const TEAM_KIND_LABELS = { college: "College", scholastic: "High school", club: "Club", pro: "Pro" };
export const JUNIOR_LABEL = "Junior";
// `junior` counts only as true (a list's .map(teamKindLabel) passes indexes).
export const teamKindLabel = (kind, junior = false) =>
  junior === true && kind !== "college" && kind !== "pro" ? JUNIOR_LABEL : TEAM_KIND_LABELS[kind] || "";

// The college level a college athlete picks (stored as the code).
export const COLLEGE_LEVELS = [
  ["FR", "Freshman"], ["SO", "Sophomore"], ["JR", "Junior"], ["SR", "Senior"], ["GR", "Graduate"],
];
const COLLEGE_LEVEL_WORDS = { FRESHMAN: "FR", SOPHOMORE: "SO", JUNIOR: "JR", SENIOR: "SR", GRADUATE: "GR" };
// A college level code from a stored value or a Cerebro class standing
// ("JR", "jr", "Junior"); "" when it names none.
export const collegeLevelOf = (raw) => {
  const v = String(raw ?? "").trim().toUpperCase();
  if (COLLEGE_LEVELS.some(([code]) => code === v)) return v;
  return COLLEGE_LEVEL_WORDS[v] || "";
};

// ---- Cerebro pre-fills -------------------------------------------------------
// The hub fills blanks from the athlete's Cerebro match so they confirm
// instead of typing. Until they save, the fields show (and count) those
// values; users/profile_strength.py (hub_view) applies the same fills to the
// stored profile, so the hub, the athlete's Home and the coach's "Profiles
// complete" count agree for one athlete. Keep the two in step.
//
// Only a bio the server trusts fills anything (trustedBio): athlete/by-email
// says `bioTrusted` when the stats doc is bound to the athlete or to a record
// known to be theirs, never for a bare name match, which can be a same-named
// stranger's.
export const trustedBio = (info) => {
  const bio = info?.cerebroBio;
  return info?.bioTrusted === true && bio && typeof bio === "object" && !Array.isArray(bio) ? bio : {};
};
// What the identity took from the stats doc (its seasons and its bio): left
// out of the hub's fills when that doc is not trusted.
const STATS_SOURCES = new Set(["seasons", "bio"]);
const fromStats = (item) => STATS_SOURCES.has(item?.source);
const sameName = (x, y) => String(x || "").trim().toLowerCase() === String(y || "").trim().toLowerCase();
// A class year (1950 to 2100) from a number or numeric text, else null: the
// graduation year a bio may fill (backend profile_strength._year_of).
const bioYear = (v) => {
  if (typeof v !== "number" && typeof v !== "string") return null;
  const n = typeof v === "string" ? Number(v.trim() || NaN) : v;
  return Number.isInteger(n) && n >= 1950 && n <= 2100 ? n : null;
};
// A bio jersey ("23", 23) as the number the profile stores; 0 for anything
// else: 0 and 00 cannot be stored (0 is "no jersey"), nor text or 100 and up.
const bioJersey = (v) => {
  const text = typeof v === "number" ? String(v) : typeof v === "string" ? v.trim() : "";
  if (!/^\d{1,2}$/.test(text)) return 0;
  return parseInt(text, 10);
};
const POSITION_ALIASES = {
  PG: ["point guard"], SG: ["shooting guard"], G: ["guard", "point guard", "shooting guard"], GUARD: ["guard", "point guard", "shooting guard"],
  SF: ["small forward"], PF: ["power forward"], F: ["forward", "small forward", "power forward"], FORWARD: ["forward", "small forward", "power forward"],
  C: ["center"], CENTER: ["center"],
};
// The position values ({ value, label } options) a Cerebro position names.
export const matchCerebroPosition = (cerebroPos, options) => {
  if (!cerebroPos || !options?.length) return [];
  const wanted = new Set();
  String(cerebroPos).toUpperCase().split(/[,/-]/).map((t) => t.trim()).filter(Boolean).forEach((tok) => (POSITION_ALIASES[tok] || [tok.toLowerCase()]).forEach((x) => wanted.add(x)));
  const exact = options.filter((o) => wanted.has(String(o.label || "").toLowerCase())).map((o) => o.value);
  if (exact.length) return exact;
  return options.filter((o) => { const l = String(o.label || "").toLowerCase(); return [...wanted].some((w) => w && (l.includes(w) || w.includes(l))); }).map((o) => o.value);
};

// A bio school or team as text ("Pace Academy" or { name }); "" for anything
// else, so the name fields (which trim what they hold) only ever get text.
const bioText = (v) => {
  const name = typeof v === "string" ? v : v && typeof v === "object" ? v.name : "";
  return typeof name === "string" ? name.trim() : "";
};

// The profile with its blanks filled from the Cerebro bio (`cerebroBio`;
// the hub hands it trustedBio). `sportCodes` are the sport codes, for the
// basketball code a bio implies. Weight fills a man's profile only; the
// graduation year comes from the high school class (grad_year) only, never
// the draft-eligible year; a jersey is stored as its number.
export const cerebroFills = (info = {}, sportCodes = []) => {
  const next = { ...info };
  const bio = info.cerebroBio || {};
  if (!next.height && bio.height_inches) next.height = bio.height_inches;
  if (!next.weight && bio.weight_lb && isMale(next)) next.weight = bio.weight_lb;
  if (!next.gradYear && bioYear(bio.grad_year)) next.gradYear = bioYear(bio.grad_year);
  if (!next.homeTown && bio.hometown) next.homeTown = bio.hometown;
  if (!next.dateOfBirth && bio.date_of_birth) next.dateOfBirth = String(bio.date_of_birth).slice(0, 10);
  if (!next.phoneNumber && bio.phone) next.phoneNumber = bio.phone;
  if (!next.state && bio.state_value != null) next.state = bio.state_value;
  if (!next.countryRepresented && bio.country_value != null) next.countryRepresented = bio.country_value;
  if (!next.levelOfPlay && bio.level_of_play) next.levelOfPlay = bio.level_of_play;
  // A college athlete's class standing (FR ... GR) is their college level.
  if (!collegeLevelOf(next.collegeLevel) && isCollegeAthlete(next) && collegeLevelOf(bio.class_standing)) next.collegeLevel = collegeLevelOf(bio.class_standing);
  if (!next.jerseyNumber && bioJersey(bio.jersey_number)) next.jerseyNumber = bioJersey(bio.jersey_number);
  if (!next.primarySport && (bio.position || bio.height_inches || bio.weight_lb || bio.high_school)) {
    const basketball = (sportCodes || []).find((c) => String(c.name || "").toLowerCase() === "basketball");
    if (basketball?.value) next.primarySport = basketball.value;
  }
  return next;
};

// The Team, Club and last high school the athlete's identity gives (the
// server's team slots, athlete/by-email's `identity`), by the level the hub
// edits: a college athlete's Team is their college (a college team a link
// filed in the Club field included, named by its institution), a high
// school athlete's their high school, a pro's University (`school`) their
// last college; the Club is their club (a pro's Team their pro team), never
// a college or a high school team. With `trusted` false (the stats doc is
// not trusted, trustedBio) nothing the identity took from that doc fills a
// field. Empty names without an identity. backend profile_strength.hub_view
// fills the school the same way from the primary team's kind.
export const identitySchoolNames = (athlete = {}, identity = null, trusted = true) => {
  if (!identity?.current) return { school: "", clubTeam: "", lastHighSchool: "" };
  const level = levelOf(athlete);
  const slot = (key) => {
    const item = identity.current?.[key];
    return item && (trusted || !fromStats(item)) ? String(item.name || "").trim() : "";
  };
  // A name the identity leads a history list with (a pro's University, the
  // last high school), unless that entry came from an untrusted stats doc.
  const lead = (text, list) => {
    const first = (identity.history?.[list] || [])[0];
    return !trusted && fromStats(first) && sameName(first?.name, text) ? "" : String(text || "").trim();
  };
  const school = level === 2 ? slot("college")
    : level === 1 ? slot("highSchool")
      : level === 3 ? lead(identity.school, "colleges")
        : slot("college") || slot("highSchool");
  return {
    school,
    clubTeam: level === 3 ? slot("proTeam") || slot("club") : slot("club"),
    lastHighSchool: level >= 2 ? lead(identity.lastHighSchool, "highSchools") : "",
  };
};

// The editor's history lists (name keys) and the identity's history list
// each one shows.
export const HISTORY_LISTS = [["previousClubTeams", "clubs"], ["previousColleges", "colleges"], ["previousHighSchools", "highSchools"]];

// The previous teams the identity lists (clubs, colleges, high schools; the
// last high school and a pro's University have fields of their own): what
// the hub shows in a list the athlete has not saved, until they edit it.
// With `trusted` false, what the identity took from the stats doc is left out.
export const identityHistoryNames = (identity = null, trusted = true) => Object.fromEntries(HISTORY_LISTS.map(([key, list]) => {
  const names = Array.isArray(identity?.[key]) ? identity[key] : [];
  const found = (identity?.history?.[list] || []).filter(fromStats);
  return [key, names.map((n) => String(n || "").trim()).filter((n) => n && (trusted || !found.some((t) => sameName(t?.name, n))))];
}));

// The athlete has a team besides a club: the team they are linked to, or
// their own college or high school in the school field (a pro's is their
// University, a past college). Mirrors backend profile_strength.has_team.
export const hasTeam = (a, n) => has(a?.team_id) || (levelOf(a) !== 3 && has(n?.school));

// The Team, Club and last high school Cerebro gives for blank ones, by
// athlete type (`athlete` already filled), so each lands in its own field
// once: a college athlete's current team is their college (not a club) and
// their bio high school is their last high school; a pro's University is the
// pre-draft team.
export const cerebroSchoolNames = (athlete = {}, bio = {}) => {
  const level = levelOf(athlete);
  const team = bioText(bio?.current_team);
  const school = level === 2 ? team || bioText(bio?.pre_draft_team)
    : level === 3 ? bioText(bio?.pre_draft_team)
      : bioText(bio?.high_school) || bioText(bio?.pre_draft_team);
  return { school, clubTeam: level === 2 ? "" : team, lastHighSchool: level >= 2 ? bioText(bio?.high_school) : "" };
};

// The Teams section's first two lines name the level's team (backend
// profile_strength _team_item / _club_item, the same words): the Team field
// holds a high school or college team (a pro's is their University, in
// Previous teams), and a pro's Club line is their pro team.
const TEAM_ITEMS = { 1: "Add your high school team", 2: "Add your college team", 3: "Add your university" };
const teamItem = (a) => TEAM_ITEMS[levelOf(a)] || "Add your team";
const clubItem = (a) => (levelOf(a) === 3 ? "Add your pro team" : "Add your club");

// The text a line shows for this athlete.
export const lineItem = (line, athlete = {}) => (typeof line[1] === "function" ? line[1](athlete || {}) : line[1]);

export const SECTIONS = [
  {
    id: "photo", group: "Essentials", label: "Photo & bio", icon: navCamera,
    desc: "A recent, well-lit photo is what coaches and brands see first.",
    items: [
      ["avatar", "Add a photo", (a) => has(a.avatar)],
      // Unset is the default (Cache-generated) the switch shows selected.
      ["bioSource", "Choose a bio source", () => true],
    ],
  },
  {
    id: "personal", group: "Essentials", label: "Personal details", icon: navPersonal,
    desc: "Your legal name and where you are from. Your name and hometown (city and state) appear on your public profile.",
    items: [
      ["name", "Add your name", (a) => has(a.name)],
      ["surname", "Add your surname", (a) => has(a.surname)],
      ["dateOfBirth", "Add your date of birth", (a) => has(a.dateOfBirth)],
      ["gender", "Pick a gender", (a) => has(a.gender)],
      ["countryRepresented", "Pick a country", (a) => has(a.countryRepresented)],
      ["phoneNumber", "Add a phone number", (a) => has(a.phoneNumber)],
      ["homeTown", "Add your hometown city", (a) => has(a.homeTown)],
      ["state", "Pick a state", (a) => has(a.state)],
    ],
  },
  {
    id: "sport", group: "Essentials", label: "Sport", icon: navSport,
    desc: "We pre-filled what we found. Confirm, correct, or add anything below.",
    items: [
      ["primarySport", "Pick a sport", (a) => has(a.primarySport)],
      ["levelOfPlay", "Pick a level of play", (a) => has(a.levelOfPlay)],
      ["position", "Add a position", (a) => has(a.position)],
      ["height", "Add height", (a) => has(a.height)],
      ["weight", "Add weight", (a) => has(a.weight), isMale],
    ],
  },
  {
    // "Teams" (Kobi, 2026-10-07; was "School & team"): the id stays
    // "school", so links with ?section=school keep working.
    id: "school", group: "Essentials", label: "Teams", icon: navSchool,
    desc: "Your team, your club and the teams you played for before. We pre-filled what we found. Confirm, correct, or add anything below.",
    items: [
      ["school", teamItem, (a, n) => has(n.school)],
      // Any team counts (dev call, 2026-10-07): a club, the team they are
      // linked to, or their school's team (hasTeam).
      ["clubTeam", clubItem, (a, n) => has(n.clubTeam) || hasTeam(a, n), (a) => !isCollegeAthlete(a)],
      ["lastHighSchool", "Add your last high school", (a, n) => has(n.lastHighSchool), (a) => levelOf(a) >= 2],
      ["gradYear", "Add high school graduation year", (a) => has(a.gradYear)],
      ["collegeLevel", "Pick your college level", (a) => !!collegeLevelOf(a.collegeLevel), isCollegeAthlete],
    ],
  },
  {
    id: "socials", group: "Essentials", label: "Socials & links", icon: navLinks,
    desc: "Your digital footprint feeds into your Visibility score. Only what you connect appears on your profile.",
    items: [
      ["network", "Add a social network", (a) => (a.socialMedia || []).some((u) => SOCIAL_NETWORKS.includes(platformOf(u)))],
      ["sportsLink", "Add a sports link", (a) => (a.socialMedia || []).some((u) => { const p = platformOf(u); return p && !SOCIAL_NETWORKS.includes(p); })],
    ],
  },
  {
    id: "academics", group: "IRL impact", label: "Academics", icon: navAcademics,
    desc: "What you do off the field is part of your value. More detail means a stronger CacheScore™.",
    items: [
      ["academicAwards", "Add an academic achievement", (a) => has(a.academicAwards)],
      ["enrichments", "Add an enrichment", (a) => has(a.enrichments)],
    ],
  },
  {
    id: "honors", group: "IRL impact", label: "Honors & activities", icon: navHonors, desc: "",
    items: [
      ["athleticAwards", "Add an athletic honor", (a) => has(a.athleticAwards)],
      ["activities", "Add an activity", (a) => has(a.activities)],
    ],
  },
  {
    id: "work", group: "IRL impact", label: "Work & NIL deals", icon: navWork, desc: "",
    items: [
      ["internships", "Add an internship", (a) => has(a.internships)],
    ],
  },
  { id: "privacy", group: "Settings", label: "Privacy", icon: navPrivacy, desc: "", setting: true, items: [] },
];

export const GROUPS = ["Essentials", "IRL impact", "Settings"];

// The lines of a section that count for this athlete.
export const sectionLines = (section, athlete = {}, names = {}) =>
  section.items.filter(([, , , applies]) => !applies || applies(athlete || {}, names || {}));

// `missing` holds the lines still to do, each with its text for this
// athlete (lineItem) in place of the second entry.
export const sectionProgress = (section, athlete = {}, names = {}) => {
  if (section.setting) return { done: 0, total: 0, pct: 100, complete: true, missing: [] };
  const lines = sectionLines(section, athlete, names);
  const missing = lines
    .filter(([, , ok]) => !ok(athlete || {}, names || {}))
    .map((line) => [line[0], lineItem(line, athlete), line[2], line[3]]);
  const total = lines.length;
  const done = total - missing.length;
  return { done, total, pct: total ? Math.round((done / total) * 100) : 100, complete: missing.length === 0, missing };
};

export const hubProgress = (athlete = {}, names = {}) => {
  let done = 0;
  let total = 0;
  let sectionsLeft = 0;
  let upNext = null;
  SECTIONS.forEach((s) => {
    const p = sectionProgress(s, athlete, names);
    done += p.done;
    total += p.total;
    if (!p.complete) {
      sectionsLeft += 1;
      if (!upNext) upNext = { section: s, item: p.missing[0][1] };
    }
  });
  return { pct: total ? Math.round((done / total) * 100) : 100, sectionsLeft, upNext };
};

// The support line under each section on the phone hub (ProfileSectionRow ·
// Mobile, D-e10): the first thing left to do while a section is unfinished,
// then a short summary of what is on file, built only from the profile
// (e.g. "Kobi Wu · Las Vegas", "GPA 3.84 · 3 items"). Privacy always reads
// "What people can see". Empty when there is nothing to show. `codeMap` is
// the code lists by type and value (useCodeMaps), for the sport and position.
const SUMMARY_PLATFORMS = [
  ["instagram", "Instagram"], ["tiktok", "TikTok"], ["youtube", "YouTube"], ["x", "X"],
  ["hudl", "Hudl"], ["247sports", "247Sports"], ["on3", "On3"], ["passport", "Passport"],
];
const countOf = (v) => (Array.isArray(v) ? v.length : 0);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const joinDot = (parts) => parts.map((p) => (p == null ? "" : String(p).trim())).filter(Boolean).join(" · ");

export const sectionSummary = (section, athlete = {}, names = {}, codeMap = {}) => {
  if (section.setting) return "What people can see";
  const p = sectionProgress(section, athlete, names);
  if (!p.complete) return p.missing[0][1];
  const a = athlete || {};
  switch (section.id) {
    case "photo":
      // The bio and interests are optional: named only when there.
      return ["Photo", has(a.bio) ? "bio" : "", countOf(a.interests) ? plural(countOf(a.interests), "interest", "interests") : ""].filter(Boolean).join(", ");
    case "personal":
      return joinDot([[a.name, a.surname].map((x) => String(x || "").trim()).filter(Boolean).join(" "), a.homeTown]);
    case "sport": {
      const sport = codeMap?.sport?.[a.primarySport];
      const first = Array.isArray(a.position) ? a.position[0] : undefined;
      const position = (sport?.positions || []).find((x) => String(x.value) === String(first));
      return joinDot([sport?.name, position?.name]);
    }
    case "school": {
      // The Team first (a pro's is their pro team), then the class.
      const team = levelOf(a) === 3 ? names.clubTeam : names.school || names.clubTeam;
      return joinDot([team, isCollegeAthlete(a) ? collegeLevelOf(a.collegeLevel) : a.gradYear]);
    }
    case "socials": {
      const found = new Set();
      let others = 0;
      (a.socialMedia || []).forEach((u) => {
        const k = platformOf(u);
        if (k === "other") others += 1;
        else if (k) found.add(k);
      });
      const labels = SUMMARY_PLATFORMS.filter(([k]) => found.has(k)).map(([, label]) => label);
      if (others) labels.push(plural(others, "other link", "other links"));
      return labels.join(", ");
    }
    case "academics":
      return joinDot([has(a.gpa) ? `GPA ${a.gpa}` : "", plural(countOf(a.academicAwards) + countOf(a.enrichments), "item", "items")]);
    case "honors":
      return joinDot([plural(countOf(a.athleticAwards), "honor", "honors"), plural(countOf(a.activities), "activity", "activities")]);
    case "work":
      // NIL deals are optional: named only when there.
      return joinDot([plural(countOf(a.internships), "internship", "internships"), countOf(a.nilDeals) ? plural(countOf(a.nilDeals), "NIL deal", "NIL deals") : ""]);
    default:
      return "";
  }
};

export const nextSection = (id) => {
  const i = SECTIONS.findIndex((s) => s.id === id);
  return i >= 0 && i < SECTIONS.length - 1 ? SECTIONS[i + 1] : null;
};

// The privacy rows (Figma "SettingRow"). Keys match the backend defaults.
export const PRIVACY_ROWS = [
  { key: "listedInPublicIndex", title: "List me under index", badge: "Free", desc: "Your profile shows up when coaches, recruiters and brands look up athletes. Requires a profile photo.", defaultValue: true, requiresAvatar: true },
  { key: "cacheScorePublic", title: "CacheScore™ on public profile", badge: "Always included", desc: "Coaches and recruiters can see your composite score.", defaultValue: true, forced: true },
  { key: "cacheValueVisible", title: "CacheValue™ on public profile", badge: "Premium feature", desc: "Show your CacheValue™ market valuation card on your public share page.", defaultValue: false },
  { key: "nilDealsVisible", title: "CACHENILx™ Value & NILx Rate Card", badge: "Premium feature", desc: "Show your CACHENILx™ Value and NILx Rate Card on your public share page.", defaultValue: false },
  { key: "socialsVisible", title: "Social profiles visible on public page", badge: "Free", desc: "Connected platforms appear on your profile. Managed per platform.", defaultValue: true },
];

export const HONOR_LEVELS = ["Local", "Regional", "National", "Global"];
export const IMPACT_LEVELS = ["Local", "Regional", "National"];
